/**
 * HOW MANY REQUESTS ARE STILL OPEN ON ONE TIER OF THE SUB BOARD.
 *
 * Bobbie, 2026-09-07 evening, circling the "Sub requests you could claim" tile
 * on her own Director home: staff should also have a tile only for Jr Staff
 * sub requests, so they can see what is open on the Jr Staff board.
 *
 * "Sub requests you could claim" cannot answer that question. For a Director
 * it counts every tier rolled into one number, and for an Executive Teacher it
 * counts her own tier only, so neither screen has ever said how the Jr Staff
 * board is doing. This counts ONE tier, and only the requests still waiting:
 * a claimed request is cover already arranged and a cancelled one is not work
 * at all, so neither is something a person could act on tonight.
 *
 * A REQUEST WITH NO TIER IS NOT AN ANSWER. Requests filed before 2026-08-06
 * carry no `tier` field at all (see lib/subs.ts's schema note), and the rules
 * deliberately hide them from ensemble and company for that reason. Counting
 * one here would tell a Director that the Jr Staff board holds something it
 * may well not, so an untagged request is counted on no tier.
 *
 * WHO MAY BE SHOWN THIS, and the rule is about the TIER ASKED FOR, not about
 * seeing every tier. Ask for a tier the viewer's own listener does not carry
 * and the count reads zero on her screen forever, which is the "control the
 * rules will refuse" trap in docs/lessons.md with the quiet failure instead
 * of the loud one. An Executive Teacher's listener carries
 * where('tier','in',['company','manager','director']) since the shared Sr
 * Staff board (2026-09-28), never 'ensemble', so asking her for 'ensemble' is
 * that trap.
 *
 * Two callers, both asking for a tier they really hold. A Director or manager
 * passes any tier: seesAllSubTiers() in lib/subs.ts, kept in lockstep with
 * subTierReachable() in shared/firestore.rules, says their read carries all
 * of them. And a Jr Staff's own sub tile (2026-09-07) passes 'ensemble' -
 * exactly the one tier her listener does carry, and the only one the rules
 * let her claim from - so it is the safe half of the same rule, not an
 * exception to it. An earlier version of this note named seesAllSubTiers as
 * the whole gate, which the ensemble caller does not satisfy and does not
 * need to.
 *
 * Pure, and it imports nothing, so a test can load it in node.
 */

export function openSubCountForTier(
  requests: readonly { status?: string; tier?: string }[],
  tier: string | null | undefined
): number {
  if (!tier) return 0;
  return requests.filter((request) => request.status === 'open' && request.tier === tier).length;
}

/**
 * THE ONE NUMBER A JR STAFF'S SUB TILE SAYS, in Bobbie's own words
 * (2026-09-07 evening): "N subs needed", and "1 sub needed" for one.
 *
 * Here rather than beside the tile so it can be tested without a bundler,
 * and beside the count it describes so the number and the way it is said stay
 * one thing. The tile used to print this count as a big number AND a second
 * sentence carrying openSubCountForTier above - two true answers to two
 * different questions, side by side on a child's home screen. Her ruling was
 * one number; the other count still lives on the board itself.
 */
export function subsNeededLabel(count: number): string {
  return `${count} ${count === 1 ? 'sub' : 'subs'} needed`;
}
